/**
 * 大海龟旅店 — 姿态映射：把剧情里的「表情 / 说话 / 休息」翻译成正式图集里真实存在的状态帧。
 *
 * 资产管线导出的图集（manifest key `actor-<id>`，帧名 `<state>-<dir>-<n>`）各自只有需要的状态：
 *   罗大湖  idle · sit · sittalk(坐着指点) · dim(低头，眼睛暗) · talk(=sittalk)   吴庸  idle · sit · sittalk(拍膝大笑) · boast(站着张臂大喊) · talk
 *   老板    idle · talk(说大话的手势) · wipe(擦杯)                              小二  idle · talk · carry(端酒)
 *   酒客 a–g（全部坐姿）idle · sit · drink · laugh · talk                        站着喝的酒客 barfly a/b、客人 crowd a/b  idle · talk · laugh
 *   水手 a/b · 女孩 a/b · 老者 a/b · 客人 crowd c/d  idle · walk · talk（站立）
 * 规则：坐着的人休息姿态是 sit，老板是 wipe，其余 idle；说话时坐着用 sittalk（绝不站起来）、站着用 talk，喊叫用 boast；
 * 表情优先用身体帧（laugh / dim / drink / sittalk …），图集没有该帧时才退回气泡。选择逻辑是纯函数（可单测）；
 * 开发占位图集缺帧时 characters 模块自己的回退链（dim→sit→idle …）兜底，从不报错。
 */
import type Phaser from 'phaser';
import { FACINGS, forgetActorAnims, frameName } from '@/characters/anims';

export type PoseChain = readonly string[];

/** 表情 → 姿态候选（按顺序取图集里第一个存在的状态）。`hold` = 保持为新的休息姿态；`bubble` = 气泡策略。 */
export interface PoseRule { seated: PoseChain; standing: PoseChain; hold?: boolean; bubble: 'always' | 'fallback' }

/**
 * 有身体帧就用帧；`fallback` 类表情（笑、暗淡、喝酒、涨红）只在没有帧时才弹气泡，
 * `always` 类（惊叹、疑问、不安、皱眉、摇头、努嘴、怒）带信息，气泡照旧。
 */
export const POSE_FOR_EMOTE: Readonly<Record<string, PoseRule>> = {
  laugh: { seated: ['laugh', 'sittalk'], standing: ['laugh'], bubble: 'fallback' },
  dim: { seated: ['dim'], standing: ['dim'], hold: true, bubble: 'fallback' },
  drink: { seated: ['drink'], standing: ['drink'], bubble: 'fallback' },
  flushed: { seated: ['sittalk'], standing: ['talk'], bubble: 'fallback' },
  happy: { seated: ['sittalk'], standing: ['talk'], bubble: 'fallback' },
  smug: { seated: ['sittalk'], standing: ['boast', 'talk'], bubble: 'always' },
  angry: { seated: ['sittalk'], standing: ['boast', 'talk'], bubble: 'always' },
  exclaim: { seated: ['sittalk'], standing: ['boast', 'talk'], bubble: 'always' },
  question: { seated: ['sittalk'], standing: ['talk'], bubble: 'always' },
  shake: { seated: [], standing: ['talk'], bubble: 'always' },
  unease: { seated: [], standing: [], bubble: 'always' },
  frown: { seated: [], standing: [], bubble: 'always' },
};

/** 坐着的人说话：只许 sittalk（没有就保持 sit）——绝不用站姿 talk 帧，否则主角会从凳子上「弹起来」。 */
export const TALK_SEATED: PoseChain = ['sittalk', 'sit'];
export const TALK_STANDING: PoseChain = ['talk', 'idle'];
/** 站着喊：吴庸的 boast（张臂大喊），其他人退回 talk。 */
export const SHOUT_STANDING: PoseChain = ['boast', 'talk', 'idle'];
const SHOUT_EMOTIONS: ReadonlySet<string> = new Set(['shout', 'smug', 'scorn', 'angry']);

export function talkChain(seated: boolean, emotion?: string): PoseChain {
  if (seated) return TALK_SEATED;
  return emotion && SHOUT_EMOTIONS.has(emotion) ? SHOUT_STANDING : TALK_STANDING;
}

/** 休息姿态：坐着 → sit；老板 → wipe（“老板正拿一块看不出颜色的布擦着杯子”）；其余 idle。 */
export function restPose(id: string, seated: boolean): string {
  if (seated) return 'sit';
  return id === 'inn-boss' ? 'wipe' : 'idle';
}

/** 候选链里第一个图集真有的状态；都没有 → null（调用方决定退回气泡或休息姿态）。 */
export function pickPose(has: (state: string) => boolean, chain: PoseChain): string | null {
  for (const s of chain) if (has(s)) return s;
  return null;
}

/** 内容表情名 → 正式气泡图（manifest `emote-*`：exclaim question sweat heart dots anger note bulb）。赵呈博的“眉头紧缩……似乎在思索什么”用 dots（…）。 */
export const EMOTE_ICON_ALIAS: Readonly<Record<string, string>> = { angry: 'anger', unease: 'sweat', flushed: 'sweat', shake: 'dots', frown: 'dots' };

/** 有正式别名图就用别名（`emote-anger`），否则用原名（本场景的占位图或 characters 的开发图）。 */
export function emoteIcon(has: (textureKey: string) => boolean, emote: string): string {
  const alias = EMOTE_ICON_ALIAS[emote];
  return alias && has(`emote-${alias}`) ? alias : emote;
}

// ------------------------------------------------------------------ 零星动作（确定性，SeededRng 取值）
/** 坐着的酒客每隔一阵举杯（drink 帧）。 */
export const DRINK_EVERY_MS: readonly [number, number] = [5000, 11000];
export const DRINK_MS = 2400;
/** 闲聊哄笑时声源附近的人一起笑。 */
export const LAUGH_MS = 1300;
export const TABLE_LAUGH_RADIUS = 56;

// ------------------------------------------------------------------ 正式图集的运行期适配
/**
 * 小二的图集没有 walk 帧（管线未要求，见 docs/asset-requests/inn-builder.md），但有 carry（端着酒）：
 * 把 carry 帧登记为 walk 别名，穿梭桌间时就端着酒走。幂等；图集已有 walk 或纹理未加载时不做任何事。
 * 别名动画的帧率取自图集 meta（写回 `meta.states.walk`，characters 的 ensureActorAnims 会读它）。
 */
export function aliasWalkFrames(scene: Phaser.Scene, texture: string, from = 'carry', fps = 5): boolean {
  if (!scene.textures.exists(texture)) return false;
  const tex = scene.textures.get(texture);
  if (tex.has(frameName('walk', 'down', 0)) || !tex.has(frameName(from, 'down', 0))) return false;
  let added = false;
  for (const facing of FACINGS) {
    for (let n = 0; n < 8; n++) {
      const src = frameName(from, facing, n);
      if (!tex.has(src)) break;
      const f = tex.get(src);
      if (tex.add(frameName('walk', facing, n), f.sourceIndex, f.cutX, f.cutY, f.cutWidth, f.cutHeight)) added = true;
    }
  }
  if (!added) return false;
  const cd = (tex as { customData?: { meta?: { states?: Record<string, { frames?: number; fps?: number }> } } }).customData;
  if (cd?.meta?.states && !cd.meta.states['walk']) cd.meta.states['walk'] = { ...(cd.meta.states[from] ?? {}), fps };
  forgetActorAnims(texture);
  return true;
}
